import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { 
  Users, FileText, Hospital, Truck, Settings, LogOut, Menu, X, Search,
  BarChart2, Calendar, Shield, Database, Bell, ChevronDown, ChevronRight,
  Moon, Sun, HelpCircle, User, Home, ChevronLeft
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifications, setNotifications] = useState([
    { id: 1, message: "New donor registration", time: "5 min ago", read: false },
    { id: 2, message: "Emergency request #2845", time: "20 min ago", read: false },
    { id: 3, message: "Low inventory alert: O-", time: "1 hour ago", read: true }
  ]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Close mobile menu on location change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    // You would implement actual dark mode toggle logic here
    document.documentElement.classList.toggle('dark');
  };

  const toggleSubmenu = (label) => {
    setActiveSubmenu(activeSubmenu === label ? null : label);
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const menuItems = [
    {
      icon: BarChart2,
      label: 'Dashboard',
      path: '/admin/dashboard',
    },
    {
      icon: Users,
      label: 'Donors',
      path: '/admin/dashboard/donors',
      hasSubmenu: true,
      submenu: [
        { label: 'All Donors', path: '/admin/dashboard/donors' },
        { label: 'Add New Donor', path: '/admin/dashboard/donors/form' }
      ]
    },
    {
      icon: FileText,
      label: 'Requests',
      path: '/admin/dashboard/requests',
      hasSubmenu: true,
      submenu: [
        { label: 'All Requests', path: '/admin/dashboard/requests' },
        { label: 'Add New Request', path: '/admin/dashboard/requests/form' },
        { label: 'Emergency Requests', path: '/admin/dashboard/requests/emergency' }
      ]
    },
    {
      icon: Hospital,
      label: 'Hospitals',
      path: '/admin/dashboard/hospitals',
    },
    {
      icon: Truck,
      label: 'Vehicle Management',
      path: '/admin/dashboard/vehicles',
    },
    {
      icon: Calendar,
      label: 'Events',
      path: '/admin/dashboard/events',
    },
    {
      icon: Database,
      label: 'Blood Inventory',
      path: '/admin/dashboard/inventory',
    },
    {
      icon: Shield,
      label: 'Admin Users',
      path: '/admin/dashboard/users',
    },
    {
      icon: Settings,
      label: 'Settings',
      path: '/admin/dashboard/settings',
    }
  ];

  const unreadNotifications = notifications.filter(n => !n.read).length;

  return (
    <div className={`flex h-screen bg-gray-50 ${darkMode ? 'dark' : ''}`}>
      {/* Sidebar - Desktop */}
      <aside 
        className={`fixed inset-y-0 z-50 flex flex-col transition-all duration-300 ease-in-out
          ${collapsed ? 'w-20' : 'w-64'} 
          bg-white dark:bg-gray-900 shadow-lg
          ${mobileMenuOpen ? 'left-0' : '-left-full lg:left-0'}`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-800">
          <Link to="/admin/dashboard" className="flex items-center">
            {!collapsed && (
              <span className="text-xl font-bold text-red-600 ml-2">BloodHero Admin</span>
            )}
            {collapsed && (
              <span className="text-xl font-bold text-red-600">BH</span>
            )}
          </Link>
          <button 
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 focus:outline-none"
          >
            {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* Sidebar Menu */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <div key={item.label}>
              {item.hasSubmenu ? (
                <div>
                  <button
                    onClick={() => toggleSubmenu(item.label)}
                    className={`flex items-center w-full px-3 py-2 rounded-md transition-colors duration-200 group
                      ${isActive(item.path) ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' : 
                      'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`}
                  >
                    <item.icon size={20} className="flex-shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="ml-3 flex-1 whitespace-nowrap">{item.label}</span>
                        <ChevronDown 
                          size={16} 
                          className={`transition-transform duration-200 ${activeSubmenu === item.label ? 'rotate-180' : ''}`} 
                        />
                      </>
                    )}
                  </button>
                  
                  {!collapsed && activeSubmenu === item.label && (
                    <div className="pl-10 mt-1 space-y-1">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.label}
                          to={subItem.path}
                          className={`block px-3 py-2 text-sm rounded-md transition-colors duration-200
                            ${isActive(subItem.path) ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' : 
                            'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'}`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  className={`flex items-center px-3 py-2 rounded-md transition-colors duration-200
                    ${isActive(item.path) ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400' : 
                    'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`}
                >
                  <item.icon size={20} className="flex-shrink-0" />
                  {!collapsed && <span className="ml-3">{item.label}</span>}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={handleLogout}
            className={`flex items-center w-full px-3 py-2 text-red-600 rounded-md hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20
              ${collapsed ? 'justify-center' : ''}`}
          >
            <LogOut size={20} />
            {!collapsed && <span className="ml-3">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ease-in-out ${collapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Top Navigation Bar */}
        <header className="sticky top-0 z-40 flex items-center justify-between h-16 px-4 bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-md lg:hidden text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Breadcrumb */}
          <div className="hidden md:flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <Home size={16} />
            <span>/</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {location.pathname.split('/').filter(Boolean).pop()?.charAt(0).toUpperCase() + 
               location.pathname.split('/').filter(Boolean).pop()?.slice(1) || 'Dashboard'}
            </span>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-3">
            {/* Search */}
            <div className="relative">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
              >
                <Search size={20} />
              </button>
              
              {searchOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-900 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 p-2">
                  <div className="flex items-center border rounded-md bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
                    <Search size={16} className="ml-2 text-gray-500 dark:text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      className="w-full p-2 bg-transparent focus:outline-none dark:text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setNotificationOpen(!notificationOpen)}
                className="p-2 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
              >
                <Bell size={20} />
                {unreadNotifications > 0 && (
                  <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                    {unreadNotifications}
                  </span>
                )}
              </button>
              
              {notificationOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                    <h3 className="font-medium dark:text-white">Notifications</h3>
                    <button className="text-sm text-red-600 dark:text-red-400 hover:underline">
                      Mark all as read
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map(notification => (
                        <div 
                          key={notification.id}
                          className={`p-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 
                            ${!notification.read ? 'bg-red-50 dark:bg-red-900/10' : ''}`}
                        >
                          <div className="flex justify-between">
                            <p className="text-sm font-medium dark:text-white">{notification.message}</p>
                            {!notification.read && (
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{notification.time}</p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                        No notifications
                      </div>
                    )}
                  </div>
                  <div className="p-2 border-t border-gray-200 dark:border-gray-800 text-center">
                    <button className="text-sm text-red-600 dark:text-red-400 hover:underline">
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 p-2 rounded-full text-gray-700 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800"
              >
                <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400">
                  <User size={16} />
                </div>
                <span className="hidden md:inline-block dark:text-white">Admin</span>
              </button>
              
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-800">
                    <p className="font-medium dark:text-white">Admin User</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">admin@bloodhero.org</p>
                  </div>
                  <div className="p-2">
                    <button className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 rounded-md">
                      <User size={16} className="mr-2" />
                      Profile
                    </button>
                    <button className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 rounded-md">
                      <Settings size={16} className="mr-2" />
                      Settings
                    </button>
                    <button className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 rounded-md">
                      <HelpCircle size={16} className="mr-2" />
                      Help
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-800 my-1"></div>
                    <button 
                      onClick={handleLogout}
                      className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-md"
                    >
                      <LogOut size={16} className="mr-2" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6 overflow-auto" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
          {children || <Outlet />}
        </main>

        {/* Footer */}
        <footer className="py-4 px-6 border-t border-gray-200 dark:border-gray-800 text-center text-sm text-gray-500 dark:text-gray-400">
          <p> 2025 BloodHero | Version 2.0.1</p>
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout;